import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { PaginationMetadata } from '@core/shared/domain';
import { NotificationService } from '@core/shared/util';
import { PagedDataFetcher, PagedDataSource } from '@core/ui';

import { ArticleRange, ArticleRangeInfo, CreateArticleRange, UpdateArticleRange } from '../models';
import { ArticleRangeService } from '../services';

type FetchParams = { searchTerm: string };

@Injectable()
export class ArticleRangesFacade {
  public readonly dataSource: PagedDataSource<ArticleRangeInfo, FetchParams>;

  private readonly _pagination$: BehaviorSubject<PaginationMetadata | undefined> = new BehaviorSubject<
    PaginationMetadata | undefined
  >(undefined);
  public readonly pagination$: Observable<PaginationMetadata | undefined> = this._pagination$.asObservable();

  private readonly _articleRangeDetails$ = new BehaviorSubject<ArticleRange | null>(null);
  public readonly articleRangeDetails$ = this._articleRangeDetails$.asObservable();

  private readonly _isLoadingArticleRangeDetails$ = new BehaviorSubject(false);
  public readonly isLoadingArticleRangeDetails$ = this._isLoadingArticleRangeDetails$.asObservable();

  constructor(
    private readonly service: ArticleRangeService,
    private readonly toaster: NotificationService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.dataSource = this.buildDataSource(service);
  }

  private buildDataSource(service: ArticleRangeService): PagedDataSource<ArticleRangeInfo, FetchParams> {
    const fetcher: PagedDataFetcher<ArticleRangeInfo, FetchParams> = (pagination, { searchTerm }) =>
      service.getArticleRangeInfos({ searchTerm, ...pagination }).pipe(
        tap(({ pagination }) => {
          this._pagination$.next(pagination);
        }),
        map(({ data, pagination }) => ({
          results: data,
          hasMore: pagination.currentPage < pagination.totalPageCount,
        })),
      );

    const initialParams = { searchTerm: '' };

    return new PagedDataSource<ArticleRangeInfo, FetchParams>(fetcher, initialParams);
  }

  public loadArticleRangeDetails(articleRangeId: string, forceReload = true): void {
    const currentArticleRange = this._articleRangeDetails$.getValue();

    if (!articleRangeId || (currentArticleRange?.articleRangeId === articleRangeId && !forceReload)) {
      return;
    }

    this._isLoadingArticleRangeDetails$.next(true);

    this.service
      .getArticleRange(articleRangeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      // eslint-disable-next-line rxjs/no-ignored-error
      .subscribe((articleRange) => {
        this._articleRangeDetails$.next(articleRange);
        this._isLoadingArticleRangeDetails$.next(false);
      });
  }

  public async createArticleRange(dto: CreateArticleRange): Promise<ArticleRange> {
    return await firstValueFrom(
      this.service.createArticleRange(dto).pipe(
        tap({
          next: (articleRange) => {
            this._articleRangeDetails$.next(articleRange);

            this.dataSource.updateQueryParams({});
            this.toaster.toastSuccess('Das Sortiment wurde erstellt.');
          },
          error: () => this.toaster.toastDanger('Fehler beim Anlegen des Sortiments'),
        }),
        takeUntilDestroyed(this.destroyRef),
      ),
    );
  }

  public async updateArticleRange(articleRangeId: string, dto: UpdateArticleRange): Promise<ArticleRange> {
    return await firstValueFrom(
      this.service.updateArticleRange(articleRangeId, dto).pipe(
        tap({
          next: (articleRange) => {
            const currentDetails = this._articleRangeDetails$.getValue();

            if (currentDetails?.articleRangeId === articleRangeId) {
              this._articleRangeDetails$.next(articleRange);
            }

            this.dataSource.updateQueryParams({});
            this.toaster.toastSuccess('Das Sortiment wurde gespeichert.');
          },
          error: () => this.toaster.toastDanger('Fehler beim Speichern des Sortiments'),
        }),
        takeUntilDestroyed(this.destroyRef),
      ),
    );
  }

  public deleteArticleRange(articleRangeId: string): void {
    this.service
      .deleteArticleRange(articleRangeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const currentDetails = this._articleRangeDetails$.getValue();

          // Remove current details if these are the deleted.
          if (currentDetails?.articleRangeId === articleRangeId) {
            this._articleRangeDetails$.next(null);
          }

          this.dataSource.updateQueryParams({});
          this.toaster.toastSuccess('Das Sortiment wurde gelöscht.');
        },
        error: () => this.toaster.toastDanger('Fehler beim Löschen des Sortiments'),
      });
  }
}
